import React, {useCallback, useMemo, useRef, useState, useEffect, use, createContext} from "react";
import PropTypes from "prop-types";
import {useMediaQuery, useTheme} from "@mui/material";
import SidebarLocationSync from "./SidebarLocationSync";
import {useApolloClient, useLazyQuery, useQuery, useSubscription} from "@apollo/client";
import {GET_ACTION_COUNT_FOR_USER} from "../../../graphql/actionQueries";
import {
  GET_UNREAD_MESSAGE_COUNT,
  SUBSCRIBE_TO_USER_MESSAGES,
} from "../../../graphql/messageQueries";
import {GET_ACTION_PHASES} from "../../../graphql/phaseQueries";
import {
  GET_NOTIFICATION_CENTER,
  GET_UNSEEN_NOTIFICATION_COUNTS,
  NOTIFICATION_PAGE_SIZE,
  SUBSCRIBE_TO_STORED_NOTIFICATIONS,
} from "../../../graphql/notificationCenterQueries";
import {useUser} from "../../user/Context/UserProvider";
import {usePermissions} from "../../../context/AuthzProvider";
import {useOrgSettings} from "../../../context/OrgSettingsProvider";
import {ALERT, sidebarWidth} from "../../../resources/environment";
import {handleSubscriptionError} from "../../../resources/utils/error";
import {mergeById} from "../../../resources/utils/arrayUtils";
import {each, filter, includes, isEmpty, max, without} from "underscore";

export const SidebarContext = createContext(null);
export const SidebarDataContext = createContext(null);
export const SidebarEphemeralContext = createContext(null);

// Extra delay after sidebar CSS transition so the notification drawer doesn't flicker
const SIDEBAR_ANIMATION_BUFFER_MS = 250;

export const SidebarProvider = ({children}) => {
  const {canAccessExerciseOrIncident, canAccessMessage} = usePermissions();
  const {user} = useUser();
  const {notificationCenterEnabled} = useOrgSettings();

  const theme = useTheme();
  const xsScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [currentPage, setCurrentPage] = useState();
  const [activeOverlay, setActiveOverlay] = useState();
  const [isSidebarHidden, setIsSidebarHidden] = useState(false);
  const [isSidebarAnimating, setIsSidebarAnimating] = useState(false);
  const [notificationDrawerOpen, setNotificationDrawerOpen] = useState(false);
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [isSidebarTextVisible, setIsSidebarTextVisible] = useState(true);
  const [sidebarMenuOpen, setSidebarMenuOpen] = useState([]);
  const [unseenNotificationCounts, setUnseenNotificationCounts] = useState({});
  const seenNotificationIdsRef = useRef(new Set());
  const apolloClient = useApolloClient();
  const currentSidebarWidth = isSidebarExpanded ? sidebarWidth.open : sidebarWidth.collapsed;

  useEffect(() => {
    if (!notificationCenterEnabled) {
      return;
    }
    const fetchCounts = async () => {
      try {
        const {data} = await apolloClient.query({query: GET_UNSEEN_NOTIFICATION_COUNTS});
        if (data?.unseenNotificationCounts) {
          setUnseenNotificationCounts(data.unseenNotificationCounts);
        }
      } catch (e) {
        console.error(e);
      }
    };
    fetchCounts();
  }, [apolloClient, notificationCenterEnabled]);

  const {data: subscribedMessageData, error: subscribedMessageError} = useSubscription(
    SUBSCRIBE_TO_USER_MESSAGES,
    {
      variables: {
        userId: parseInt(user.id, 10),
      },
      skip: !canAccessMessage,
    },
  );

  const {data: subscribedNotificationData} = useSubscription(SUBSCRIBE_TO_STORED_NOTIFICATIONS, {
    variables: {
      userId: parseInt(user.id, 10),
    },
    skip: !notificationCenterEnabled,
  });

  const {
    data: unreadData,
    refetch: refetchMessage,
    error: unreadError,
  } = useQuery(GET_UNREAD_MESSAGE_COUNT, {
    variables: {userId: user.id},
    skip: !canAccessMessage,
  });

  const {data: actionPhases, error: actionPhasesError} = useQuery(GET_ACTION_PHASES, {
    skip: !canAccessExerciseOrIncident,
  });

  const [
    getMyActionCount,
    {
      data: myActionCount,
      loading: loadingMyActionCount,
      refetch: refetchMyActionCount,
      error: myActionCountError,
    },
  ] = useLazyQuery(GET_ACTION_COUNT_FOR_USER);

  useEffect(() => {
    if (subscribedMessageError) {
      handleSubscriptionError(subscribedMessageError, "User messages");
    }
  }, [subscribedMessageError]);

  useEffect(() => {
    if (unreadError) {
      ALERT.error("Could not get Message data, please try again.");
    }
  }, [unreadError]);

  useEffect(() => {
    if (actionPhasesError) {
      console.error(actionPhasesError);
    }
  }, [actionPhasesError]);

  useEffect(() => {
    if (myActionCountError) {
      console.error(myActionCountError);
    }
  }, [myActionCountError]);

  useEffect(() => {
    if (isSidebarExpanded) {
      setIsSidebarTextVisible(true);
    } else {
      const timer = setTimeout(
        () => setIsSidebarTextVisible(false),
        theme.transitions.duration.enteringScreen,
      );
      return () => clearTimeout(timer);
    }
  }, [isSidebarExpanded, theme.transitions.duration.enteringScreen]);

  useEffect(() => {
    setIsSidebarAnimating(true);
    const timer = setTimeout(
      () => setIsSidebarAnimating(false),
      theme.transitions.duration.enteringScreen + SIDEBAR_ANIMATION_BUFFER_MS,
    );
    return () => clearTimeout(timer);
  }, [isSidebarExpanded, theme.transitions.duration.enteringScreen]);

  useEffect(() => {
    if (xsScreen) {
      setIsSidebarHidden(true);
      setIsSidebarExpanded(false);
    } else {
      setIsSidebarHidden(false);
    }
  }, [xsScreen]);

  useEffect(() => {
    if (!canAccessMessage) {
      return;
    }

    refetchMessage();
  }, [subscribedMessageData, canAccessMessage]);

  useEffect(() => {
    const items = subscribedNotificationData?.subscribeToStoredNotifications;
    if (!isEmpty(items)) {
      const newItems = filter(items, (item) => !seenNotificationIdsRef.current.has(item.id));
      each(newItems, (item) => seenNotificationIdsRef.current.add(item.id));
      if (isEmpty(newItems)) {
        return;
      }

      setUnseenNotificationCounts((prev) => {
        const next = {...prev};
        each(
          filter(newItems, (item) => !item.isSeen),
          (item) => {
            const cat = item.storedNotification.category;
            next[cat] = (next[cat] || 0) + 1;
          },
        );
        return next;
      });

      const byCategory = {};
      each(newItems, (item) => {
        const sn = item.storedNotification;
        const cat = sn.category;
        if (!byCategory[cat]) {
          byCategory[cat] = [];
        }
        byCategory[cat].push({
          __typename: "NotificationItem",
          id: item.id,
          header: sn.header,
          subtext: sn.subtext,
          initiatorName: sn.initiatorName,
          isRead: false,
          isSeen: item.isSeen,
          createdAt: sn.createdAt,
          url: sn.url,
          model: sn.model?.toLowerCase(),
          badgeText: sn.severityName,
          badgeColor: sn.severityColor,
          badgeForegroundColor: sn.severityForegroundColor,
        });
      });

      each(Object.entries(byCategory), ([category, newItems]) => {
        apolloClient.cache.updateQuery(
          {
            query: GET_NOTIFICATION_CENTER,
            variables: {category, limit: NOTIFICATION_PAGE_SIZE},
          },
          (existing) => {
            if (!existing) {
              return existing;
            }
            return {
              notificationCenter: mergeById(newItems, existing.notificationCenter),
            };
          },
        );
      });
    }
  }, [subscribedNotificationData, apolloClient]);

  const resetUnseenCounts = useCallback((category) => {
    setUnseenNotificationCounts((prev) => {
      const next = {...prev};
      delete next[category];
      return next;
    });
  }, []);

  useEffect(() => {
    if (!isEmpty(actionPhases) && !loadingMyActionCount && canAccessExerciseOrIncident) {
      const lastActionPhase = max(actionPhases.actionPhases, (actionPhase) => actionPhase.ordering);
      getMyActionCount({
        variables: {userId: user.id, lastActionPhase: lastActionPhase.id},
      });
    }
  }, [actionPhases, canAccessExerciseOrIncident]);

  const toggleSidebar = useCallback(() => {
    if (xsScreen) {
      setIsSidebarHidden((prev) => !prev);
    } else {
      setIsSidebarExpanded((prev) => !prev);
    }
  }, [xsScreen]);

  const toggleSidebarMenu = useCallback((item) => {
    setSidebarMenuOpen((current) =>
      includes(current, item) ? without(current, item) : [...current, item],
    );
  }, []);

  const contextValue = useMemo(
    () => ({
      // state
      currentPage,
      setCurrentPage,
      isSidebarHidden,
      setIsSidebarHidden,
      sidebarMenuOpen,
      setSidebarMenuOpen,
      xsScreen,
      isSidebarExpanded,
      setIsSidebarExpanded,
      isSidebarTextVisible,
      isSidebarAnimating,
      currentSidebarWidth,

      // functions
      toggleSidebar,
      toggleSidebarMenu,
    }),
    [
      currentPage,
      isSidebarHidden,
      sidebarMenuOpen,
      xsScreen,
      isSidebarExpanded,
      isSidebarTextVisible,
      isSidebarAnimating,
      currentSidebarWidth,
      toggleSidebar,
      toggleSidebarMenu,
    ],
  );

  const ephemeralValue = useMemo(
    () => ({
      activeOverlay,
      setActiveOverlay,
      notificationDrawerOpen,
      setNotificationDrawerOpen,
    }),
    [activeOverlay, notificationDrawerOpen],
  );

  const dataValue = useMemo(
    () => ({
      myActionCount,
      unreadData,
      unseenNotificationCounts,
      refetchMyActionCount,
      refetchMessage,
      resetUnseenCounts,
    }),
    [myActionCount, unreadData, unseenNotificationCounts],
  );

  return (
    <SidebarContext value={contextValue}>
      <SidebarDataContext value={dataValue}>
        <SidebarEphemeralContext value={ephemeralValue}>
          <SidebarLocationSync />
          {children}
        </SidebarEphemeralContext>
      </SidebarDataContext>
    </SidebarContext>
  );
};

SidebarProvider.propTypes = {
  children: PropTypes.any,
};

export const useSidebarLayout = () => {
  return use(SidebarContext);
};

export const useSidebarData = () => {
  return use(SidebarDataContext);
};

export const useSidebarEphemeral = () => {
  return use(SidebarEphemeralContext);
};
